/**
 * ContactPage.jsx
 * 
 * Contact page for Touchstone. Includes business name, country,
 * and domain-based support email for trust & compliance signals.
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, MapPin, Building2, Clock } from 'lucide-react';
import Footer from './Footer';

export default function ContactPage() {
  const navigate = useNavigate();

  return (
    <div className="fixed inset-0 overflow-y-auto bg-[#030303] select-text">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#030303]/80 backdrop-blur-md border-b border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link
            to="/home"
            className="text-xl text-white"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif", letterSpacing: '0.05em' }}
          >
            TOUCHSTONE
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-36 pb-16 px-6">
        <div className="max-w-3xl mx-auto">
          <h1
            className="text-4xl md:text-5xl text-white mb-4"
            style={{ fontFamily: "'Qulio Display', 'Bodoni Moda', serif" }}
          >
            Contact Us
          </h1>
          <p className="text-base text-zinc-500 mb-16 max-w-xl">
            Have a question, correction, or partnership inquiry? We'd love to hear from you.
          </p>

          {/* Contact cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            
            <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-zinc-400" />
                </div>
                <h2 className="text-lg text-white font-medium">General Inquiries</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-4">
                For questions about Touchstone, our research, or how we evaluate tools.
              </p>
              <a
                href="mailto:hello@touchstone.dev"
                className="text-white hover:underline text-sm"
              >
                hello@touchstone.dev
              </a>
            </div>

            <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-zinc-400" />
                </div>
                <h2 className="text-lg text-white font-medium">Editorial & Corrections</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-4">
                Found an error? Have a correction or dispute about a tool listing?
              </p>
              <a
                href="mailto:editorial@touchstone.dev"
                className="text-white hover:underline text-sm"
              >
                editorial@touchstone.dev
              </a>
            </div>

            <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                  <Mail className="w-5 h-5 text-zinc-400" />
                </div>
                <h2 className="text-lg text-white font-medium">Privacy & Legal</h2>
              </div>
              <p className="text-zinc-400 text-sm mb-4">
                For privacy-related requests, data inquiries, or legal matters.
              </p>
              <a
                href="mailto:privacy@touchstone.dev"
                className="text-white hover:underline text-sm"
              >
                privacy@touchstone.dev
              </a>
            </div>

            <div className="p-6 rounded-2xl border border-white/[0.06] bg-white/[0.02]">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-white/[0.04] flex items-center justify-center">
                  <Clock className="w-5 h-5 text-zinc-400" />
                </div>
                <h2 className="text-lg text-white font-medium">Response Time</h2>
              </div>
              <p className="text-zinc-400 text-sm">
                We aim to respond to all inquiries within 2 business days. For corrections or factual disputes, 
                we prioritize a response within 24 hours.
              </p>
            </div>
          </div>

          {/* Business details */}
          <div className="space-y-12 text-zinc-400 leading-relaxed">
            
            <section>
              <h2 className="text-xl text-white font-medium mb-6">Business Information</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">Touchstone Research</p>
                    <p className="text-sm text-zinc-500">Independent AI research platform</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">United States</p>
                    <p className="text-sm text-zinc-500">Founded 2025</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-zinc-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-white text-sm font-medium">hello@touchstone.dev</p>
                    <p className="text-sm text-zinc-500">Primary support email</p>
                  </div>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-xl text-white font-medium mb-4">For Tool Vendors</h2>
              <p>
                If you are a tool vendor and believe your product is inaccurately represented on Touchstone, 
                please email{' '}
                <a href="mailto:editorial@touchstone.dev" className="text-white hover:underline">
                  editorial@touchstone.dev
                </a>{' '}
                with the specific claim and supporting evidence. We review all vendor disputes within 5 business days.
              </p>
              <p className="mt-4">
                Please note: Touchstone does not accept payment for placement, reviews, or favorable coverage. 
                See our{' '}
                <Link to="/editorial-policy" className="text-white hover:underline">
                  Editorial Policy
                </Link>{' '}
                for more information.
              </p>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
